// ─── Parsers ──────────────────────────────────────────────────────────────────

import { ToolConfigForAgent } from "../../../types/types"
import { KNOWN_TOOL_FIELDS } from "./aiChatConstants"
import { ActivityEvent, AgentState, DotState, ErrorDetail, RequestDetail } from "./aiChatTypes"

// ─── Activity log helpers ──────────────────────────────────────────────────────

export function getDotState(event: ActivityEvent): DotState {
  if (['test-fail', 'invalid', 'loop-blocked'].includes(event.type)) return 'error'
  if (event.type === 'loop-success') return 'success'
  return 'normal'
}

export function tryParseDetail(raw: string): ErrorDetail | RequestDetail | Record<string, unknown> | null {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function isErrorDetail(obj: object): obj is ErrorDetail {
  return 'responseBody' in obj || ('status' in obj && 'category' in obj)
}

export function isRequestDetail(obj: object): obj is RequestDetail {
  return 'method' in obj && 'url' in obj
}

export function tryParseToolConfig(text: string): {
  tool: Record<string, unknown> | null
  shortAnswer: string | null
  agentTestArgs: Record<string, unknown> | null
} {
  let cleaned = text.trim()
  const fenceMatch = cleaned.match(/```(?:json)?\n?([\s\S]*?)\n?```/)
  if (fenceMatch) cleaned = fenceMatch[1].trim()
  const start = cleaned.indexOf('{')
  const end = cleaned.lastIndexOf('}')
  if (start === -1 || end <= start) return { tool: null, shortAnswer: null, agentTestArgs: null }
  try {
    const parsed = JSON.parse(cleaned.substring(start, end + 1))
    const testArgs = (parsed?.agentTestArgs && typeof parsed.agentTestArgs === 'object')
      ? parsed.agentTestArgs as Record<string, unknown>
      : null
    if (parsed?.json?.tools?.[0]) return { tool: parsed.json.tools[0], shortAnswer: parsed.shortAnswer ?? null, agentTestArgs: testArgs }
    if (parsed?.tools?.[0]) return { tool: parsed.tools[0], shortAnswer: null, agentTestArgs: testArgs }
    if (testArgs) return { tool: null, shortAnswer: null, agentTestArgs: testArgs }
  } catch {}
  return { tool: null, shortAnswer: null, agentTestArgs: null }
}

export function isQueryMapItem(obj: Record<string, unknown>): boolean {
  return typeof obj.key === 'string' && 'value' in obj
    && !Object.keys(obj).some(k => KNOWN_TOOL_FIELDS.has(k))
}

export function extractPatchFromObject(obj: Record<string, unknown>, depth = 0): Record<string, unknown> | null {
  if (depth > 3) return null
  if ('package' in obj || 'tools' in obj) return null
  if (Object.keys(obj).some(k => KNOWN_TOOL_FIELDS.has(k))) return obj
  if (isQueryMapItem(obj)) return { queryMap: [obj] }
  for (const val of Object.values(obj)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const found = extractPatchFromObject(val as Record<string, unknown>, depth + 1)
      if (found) return found
    }
  }
  return null
}

export function tryParseFieldPatch(text: string): Record<string, unknown> | null {
  const cleaned = text.trim()
  if (cleaned.trimStart().startsWith('[')) {
    try {
      const arr = JSON.parse(cleaned)
      if (Array.isArray(arr) && arr.length > 0 && arr.every(isQueryMapItem)) {
        return { queryMap: arr }
      }
    } catch {}
  }
  const start = cleaned.indexOf('{')
  const end = cleaned.lastIndexOf('}')
  if (start === -1 || end <= start) return null
  try {
    const obj = JSON.parse(cleaned.substring(start, end + 1))
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
    return extractPatchFromObject(obj)
  } catch {}
  return null
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}



export function agentStateLabel(state: AgentState): string {
  switch (state) {
    case 'generating': return 'Generating'
    case 'validating': return 'Validating'
    case 'testing': return 'Testing'
    case 'repairing': return 'Repairing'
    case 'success': return 'Success'
    case 'blocked': return 'Blocked'
    case 'awaiting-approval': return 'Waiting for approval'
    default: return ''
  }
}


export function buildToolConfigForAgent(formValues: Record<string, unknown>): ToolConfigForAgent {
  const parseJsonField = (val: unknown): Record<string, unknown> | null => {
    if (!val || typeof val !== 'string') return null
    try { return JSON.parse(val) } catch { return null }
  }
  return {
    name: formValues.name as string | undefined,
    title: formValues.title as string | undefined,
    description: formValues.description as string | undefined,
    httpMethod: formValues.httpMethod as string | undefined,
    path: formValues.path as string | undefined,
    inputSchema: parseJsonField(formValues.inputSchema) ?? (formValues.inputSchema as any),
    queryMap: formValues.queryMap as any,
    headerMap: parseJsonField(formValues.headerMap) as Record<string, string> | undefined,
    bodyTemplate: formValues.bodyTemplate as string | undefined,
    responseTemplate: formValues.responseTemplate as string | undefined,
    mainConnectionId: formValues.mainConnectionId as string | undefined,
    annotations: parseJsonField(formValues.annotations) ?? undefined,
    sampleResponse: formValues.sampleResponse as string | undefined,
    version: formValues.version as string | undefined,
  }
}

export function isToolSufficientlyComplete(formValues: Record<string, unknown>): boolean {
  return !!(
    formValues.name &&
    formValues.description &&
    formValues.httpMethod &&
    formValues.mainConnectionId
  )
}